import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';

import { EmptyList } from '@components/EmptyList';
import { Pagination } from '@components/ui/pagination';
import { ELanguages } from '@enums/languages.enum';
import { EQueryKeys } from '@enums/queryKeys.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { formatFullName } from '@formatters/fullName.formatter';
import {
  getAllProvidersSchema,
  type GetAllProvidersParams,
} from '@services/providers/dtos/getAll.dto';
import { providerService } from '@services/providers/service';

import { ProviderFilters } from './@components/Filters';
import { ProviderTableCell } from './@components/TableCell';

function providersQueryOptions(params: GetAllProvidersParams) {
  return queryOptions({
    staleTime: EStaleTime.OneHour,
    queryKey: [EQueryKeys.Providers, params],
    queryFn: () => providerService.getAll(params),
  });
}

export const Route = createFileRoute('/_layout/(private)/providers/')({
  component: Providers,
  validateSearch: search => getAllProvidersSchema.parse(search),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(providersQueryOptions(deps)),
});

function Providers() {
  const searchParams = Route.useSearch();
  const { data } = useSuspenseQuery(providersQueryOptions(searchParams));

  return (
    <div className="relative">
      <ProviderFilters />
      <div
        className="bg-background dark:border-primary-light-one mb-2 grid w-full overflow-hidden rounded-xl border border-zinc-200 shadow"
        role="table"
      >
        <div
          className="bg-primary-light-two dark:text-primary-dark-one grid h-12 grid-cols-6 items-center font-medium text-zinc-600"
          role="row"
        >
          {headers.map(header => (
            <span
              className="text-center text-sm font-medium"
              key={header.accessorKey}
              role="columnheader"
            >
              {header.title}
            </span>
          ))}
        </div>

        {data.list.map(row => (
          <Link
            className="bg-row-odd even:bg-row-even hover:bg-row-odd-hover even:hover:bg-row-even-hover grid min-h-14 grid-cols-6 items-center justify-between transition-all duration-200"
            key={row.id}
            params={{ id: row.id }}
            role="row"
            to="/providers/$id"
          >
            <ProviderTableCell type="id" value={row.displayId} />
            <ProviderTableCell type="date" value={row.createdAt} />
            <ProviderTableCell value={row.companyName} />

            <ProviderTableCell
              value={formatFullName({
                firstName: row.firstName,
                lastName: row.lastName,
              })}
            />

            <ProviderTableCell
              value={(row.language || ELanguages.Fr).toUpperCase()}
            />
            <ProviderTableCell type="tag" value={row.archived} />
          </Link>
        ))}

        <EmptyList
          alt="No providers animation/image"
          isVisible={!data.list.length}
          title="No providers yet."
        />
      </div>

      <div className="ml-auto flex w-fit items-center">
        <span className="mr-4 text-sm text-gray-500 dark:text-gray-300">
          Total: {data.count || 0} providers(s)
        </span>
        <Pagination
          currentPage={data.pagination.page}
          totalPages={data.pagination.totalPages}
        />
      </div>
    </div>
  );
}

const headers = [
  { title: 'ID', accessorKey: 'displayId' },
  { title: 'Creation date', accessorKey: 'createdAt' },
  { title: 'Company', accessorKey: 'companyName' },
  { title: 'Name', accessorKey: 'fullName' },
  { title: 'Language', accessorKey: 'language' },
  { title: 'Status', accessorKey: 'status' },
];
